
import {
    Box,
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    Avatar,
    Breadcrumbs,
    Link as LinkMUI} from '@mui/material';
import {
    Phone as PhoneIcon,
    Email as EmailIcon,
    LocationOn as LocationIcon,
    NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import { ThemeProvider } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';
import { getAppTheme } from '../services/authContext';
import { contactApi } from '../services/mle-api'; // Import your actual API
import { useSnackbar } from '../contexts/SnackbarContext'; // Assuming you have this context
import FormikForm from '../components/Utility/FormikForm'; // Assuming this path
import { Link } from 'react-router-dom';
import React, { useState } from 'react';
import SEO from '../components/SEO/SEO';
import { getSeoData } from '../utils/seoConfig';


// --- REUSABLE COMPONENTS ---
const InfoCard = ({ icon, title, details }) => {
    return (
        <Card sx={{ height: '100%', display: 'flex', alignItems: 'center', p: 2, border: 'none', boxShadow: 'none', bgcolor: 'transparent' }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56, mr: 2 }}>
                {React.cloneElement(icon, { sx: { color: 'white' } })}
            </Avatar>
            <Box>
                <Typography variant="h6" component="h3">{title}</Typography>
                {details.map((detail, index) => {
                    let href = detail.includes('@') ? `mailto:${detail}` : detail.startsWith('+') ? `tel:${detail.replace(/\s/g, '')}` : null;
                    return (
                        <Typography key={index} variant="body2" color="text.secondary">
                           {href ? <LinkMUI href={href} underline="hover" color="inherit">{detail}</LinkMUI> : detail}
                        </Typography>
                    )
                })}
            </Box>
        </Card>
    );
};

// --- MAIN CONTACT PAGE COMPONENT ---
const Contact = () => {
    const mode = useSelector((state) => state.theme.mode);
    const theme = getAppTheme(mode);
    const { showSnackbar } = useSnackbar(); // Mocked for demonstration
    const [isSubmitting, setIsSubmitting] = useState(false);
    const seoData = getSeoData("/contact");

    const contactInfo = [
        { icon: <PhoneIcon />, title: "Call Now", details: ["+91 9266961818", "+91 9266961818"] },
        { icon: <EmailIcon />, title: "Email Address", details: ["contact@makeloaneazy.com", "support@makeloaneazy.com"] },
        { icon: <LocationIcon />, title: "Location", details: ["LG-1330 Sec-46, Gurgaon, Haryana"] }
    ];

    const initialValues = {
        name: '',
        email: '',
        phone: '',
        message: ''
    };

    const validationSchema = Yup.object({
        name: Yup.string().required('Your name is required'),
        email: Yup.string().email('Invalid email address').required('Email is required'),
        phone: Yup.string().matches(/^\+?(\d{1,3})?[-.\s]?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4,9}$/, 'Please enter a valid phone number').required('Phone number is required'),
        message: Yup.string().required('Message cannot be empty'),
    });

    const formFields = [
        { name: 'name', label: 'Your Name', type: 'text' },
        { name: 'email', label: 'Your Email', type: 'email' },
        { name: 'phone', label: 'Phone Number', type: 'tel' },
        { name: 'message', label: 'Your Message', type: 'text', multiline: true, rows: 4 },
    ];

    const handleSubmit = async (values, { resetForm }) => {
        setIsSubmitting(true);
        try {
            await contactApi.create(values);
            showSnackbar('Thank you for your message! We will get back to you soon.', { severity: 'success' });
            resetForm();
        } catch (error) {
            console.error("Form Submission Error:", error);
            showSnackbar('Failed to send message. Please try again later.', { severity: 'error' });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <ThemeProvider theme={theme}>
            <SEO {...seoData} />
            <Box sx={{ bgcolor: 'background.default', color: 'text.primary', py: 5 }}>
                <Container maxWidth="lg">
                    <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 4 }}>
                        <LinkMUI component={Link} to="/" underline="hover" color="inherit">Home</LinkMUI>
                        <Typography color="text.primary">Contact Us</Typography>
                    </Breadcrumbs>
                    
                    <Box sx={{ textAlign: 'center', mb: 8 }}>
                        <Typography variant="h2" component="h1" sx={{ color: 'heading.h2', mb: 1 }}>Contact Us</Typography>
                        <Typography variant="h6" color="text.secondary">We're here to help. Reach out to us with any questions or inquiries.</Typography>
                    </Box>

                    <Grid container spacing={8} justifyContent="center">
                        <Grid item xs={12} md={8}>
                            <Card sx={{ p: { xs: 2, sm: 4 }, height: '100%' }}>
                                <CardContent>
                                    <Typography variant="h5" component="h2" gutterBottom>Send a Message</Typography>
                                    <FormikForm
                                        initialValues={initialValues}
                                        validationSchema={validationSchema}
                                        fields={formFields}
                                        nonFixButton={true}
                                        onSubmit={handleSubmit}
                                        submitButtonText="Send Message"
                                        isLoading={isSubmitting}
                                    />
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    <Box sx={{ my: 8 }}>
                        <Typography variant="h4" component="h2" sx={{ color: 'heading.h2', mb: 4, textAlign: 'center' }}>Other Ways to Reach Us</Typography>
                         <Grid container spacing={4}>
                            {contactInfo.map((info, index) => (
                                <Grid item xs={12} md={4} key={index}>
                                    <InfoCard {...info} />
                                </Grid>
                            ))}
                        </Grid>
                    </Box>

                    <Box sx={{ my: 8 }}>
                        <Typography variant="h4" component="h2" sx={{ color: 'heading.h2', mb: 4, textAlign: 'center' }}>Find Us Here</Typography>
                        <Card sx={{ overflow: 'hidden', borderRadius: '16px' }}>
                            <iframe
                                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3508.536012415814!2d77.06173931507808!3d28.43343898249673!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390d187747e66661%3A0x84f4a36e523a233!2sSector%2046%2C%20Gurugram%2C%20Haryana!5e0!3m2!1sen!2sin!4v1678886456789!5m2!1sen!2sin"
                                width="100%"
                                height="450"
                                style={{ border: 0 }}
                                allowFullScreen=""
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                title="Google Map Location of Make Loan Eazy"
                            ></iframe>
                        </Card>
                    </Box>
                </Container>
            </Box>
        </ThemeProvider>
    );
};



export default Contact